import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { tournamentApi } from "@bw/shared/api";
import { useTournamentDisplayLive } from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";

const AUTO_ROTATE_INTERVAL_MS = 15_000;
const COURTS_PER_PAGE = 6;

type Snapshot = Schema["TournamentDisplayLiveSnapshot"];
type Rotation = Schema["TournamentDisplaySelection"]["rotation"];
type PublicMatch = Schema["TournamentPublicMatch"];
type DisplayView = { rotation: Rotation; courtPage: number };

function scoreLabel(match: PublicMatch | null | undefined): string | null {
  const score = match?.score ?? [];
  return score.length
    ? score.map((game) => `${game.side1}–${game.side2}`).join(", ")
    : null;
}

function participantsLabel(
  match: PublicMatch | null | undefined,
): string | null {
  const participants = match?.participants ?? [];
  if (!participants.length) return null;
  const side = (value: "side1" | "side2") =>
    participants
      .filter((participant) => participant.side === value)
      .map((participant) => participant.display_name ?? participant.handle)
      .join(" / ");
  const side1 = side("side1");
  const side2 = side("side2");
  return side1 && side2 ? `${side1} vs ${side2}` : side1 || side2;
}

function displayCourts(snapshot: Snapshot) {
  const selectedCourtId = snapshot.display.selection.court_id;
  return selectedCourtId
    ? snapshot.courts.filter((court) => court.court_id === selectedCourtId)
    : snapshot.courts;
}

function CourtBoard({ snapshot, page }: { snapshot: Snapshot; page: number }) {
  const courts = displayCourts(snapshot);
  const pageCount = Math.max(1, Math.ceil(courts.length / COURTS_PER_PAGE));
  const boundedPage = Math.min(Math.max(page, 0), pageCount - 1);
  const firstCourt = boundedPage * COURTS_PER_PAGE;
  const visibleCourts = courts.slice(firstCourt, firstCourt + COURTS_PER_PAGE);
  return (
    <Stack aria-label="Court board" component="section" spacing={1.5}>
      {courts.length > COURTS_PER_PAGE ? (
        <Typography color="grey.300" variant="body1">
          Courts {firstCourt + 1}–
          {Math.min(firstCourt + COURTS_PER_PAGE, courts.length)} of{" "}
          {courts.length}
        </Typography>
      ) : null}
      <Box
        component="div"
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(auto-fit, minmax(300px, 1fr))",
          },
        }}
      >
        {courts.length === 0 ? (
          <Typography color="inherit" variant="h5">
            Court assignments are awaiting publication.
          </Typography>
        ) : null}
        {visibleCourts.map((court) => {
          const current = court.current_match;
          const next = court.upcoming_matches?.[0] ?? null;
          const currentParticipants = participantsLabel(current);
          const nextParticipants = participantsLabel(next);
          return (
            <Box
              key={court.court_id}
              sx={{
                border: 2,
                borderColor: "grey.500",
                borderRadius: 2,
                minWidth: 0,
                p: { xs: 2, md: 3 },
              }}
            >
              <Stack spacing={1.25}>
                <Typography
                  component="h2"
                  sx={{ fontWeight: 800 }}
                  variant="h4"
                >
                  {court.label}
                </Typography>
                {current ? (
                  <Stack spacing={0.5}>
                    <Typography color="primary.light" variant="overline">
                      Now
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }} variant="h5">
                      {current.match_label}
                    </Typography>
                    {currentParticipants ? (
                      <Typography variant="h6">
                        {currentParticipants}
                      </Typography>
                    ) : null}
                    {scoreLabel(current) ? (
                      <Typography aria-live="polite" variant="h4">
                        Score: {scoreLabel(current)}
                      </Typography>
                    ) : null}
                    {current.timing_status === "delayed" ? (
                      <Typography color="warning.light" variant="h6">
                        Delayed
                      </Typography>
                    ) : null}
                  </Stack>
                ) : (
                  <Typography color="grey.300" variant="h6">
                    Court available
                  </Typography>
                )}
                {next ? (
                  <Stack spacing={0.25}>
                    <Typography color="grey.300" variant="overline">
                      Next
                    </Typography>
                    <Typography variant="h6">{next.match_label}</Typography>
                    {nextParticipants ? (
                      <Typography variant="body1">
                        {nextParticipants}
                      </Typography>
                    ) : null}
                    {next.timing_status === "delayed" ? (
                      <Typography color="warning.light" variant="body1">
                        Delayed
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

function displayViews({
  courtCount,
  drawId,
  hasAnnouncements,
}: {
  courtCount: number;
  drawId: string | null;
  hasAnnouncements: boolean;
}): DisplayView[] {
  // A venue can have dozens of courts. Treat each bounded court page as a
  // rotation item so an all-courts TV stays legible instead of overflowing the
  // projector viewport with a single unbounded grid.
  const courtPages = Math.max(1, Math.ceil(courtCount / COURTS_PER_PAGE));
  const views: DisplayView[] = Array.from(
    { length: courtPages },
    (_, page) => ({
      rotation: "court",
      courtPage: page,
    }),
  );
  if (drawId) {
    views.push(
      { rotation: "draw", courtPage: 0 },
      { rotation: "standings", courtPage: 0 },
    );
  }
  if (hasAnnouncements) views.push({ rotation: "ticker", courtPage: 0 });
  return views;
}

function bracketPhaseLabel(
  phase: string | null | undefined,
  phaseLabel: string | null | undefined,
) {
  if (phaseLabel) return phaseLabel;
  // Legacy cached rows can predate phase_label. This fallback only humanizes
  // their persisted phase token; it never derives a graph route or rank.
  const placesPhase = phase?.match(/^places_(\d+)_(\d+)$/);
  if (placesPhase) return `Places ${placesPhase[1]}–${placesPhase[2]}`;
  if (phase === "main") return "Main bracket";
  if (phase === "third_place") return "Third-place playoff";
  if (phase === "placement_r1") return "Placement round 1";
  if (phase === "placement_a_r2") return "Placement round 2 · A path";
  if (phase === "placement_c_r2") return "Placement round 2 · C path";
  if (phase === "placement_a_r3") return "Placement round 3 · A path";
  if (phase === "placement_b_r3") return "Placement round 3 · B path";
  if (phase === "placement_c_r3") return "Placement round 3 · C path";
  if (phase === "placement_d_r3") return "Placement round 3 · D path";
  if (phase === "flight_a") return "Flight A";
  if (phase === "flight_b") return "Flight B";
  if (phase === "flight_c") return "Flight C";
  if (phase === "flight_d") return "Flight D";
  if (phase === "consolation") return "Consolation";
  if (phase === "winner_bracket") return "Winners bracket";
  if (phase === "loser_bracket") return "Losers bracket";
  if (phase === "grand_final") return "Grand final";
  if (phase === "grand_final_reset") return "Grand final reset";
  if (phase === "knockout") return "Knockout bracket";
  return null;
}

function DrawRotation({ drawId }: { drawId: string | null }) {
  const bracket = useQuery({
    queryKey: ["venue-display", "bracket", drawId],
    queryFn: () => tournamentApi.getBracket(drawId as never),
    enabled: Boolean(drawId),
    retry: false,
  });
  if (!drawId) {
    return <Typography variant="h5">Select a draw from Displays.</Typography>;
  }
  if (bracket.isPending)
    return <Typography role="status">Loading draw…</Typography>;
  if (bracket.isError) {
    return (
      <Alert severity="error">
        The selected draw is temporarily unavailable.
      </Alert>
    );
  }
  const allMatches = bracket.data ?? [];
  // A group-to-knockout draw already has a separate standings rotation. Its
  // draw rotation therefore shows the canonical downstream knockout graph, not
  // a duplicate list of pool matches. Existing pure RR/single-elim rows retain
  // their prior rendering because they have no phase="knockout" rows.
  const matches = allMatches.some((match) => match.phase === "knockout")
    ? allMatches.filter((match) => match.phase === "knockout")
    : allMatches;
  const phaseSections = new Map<string, typeof matches>();
  for (const match of matches) {
    const phase = match.phase ?? "main";
    const section = phaseSections.get(phase) ?? [];
    section.push(match);
    phaseSections.set(phase, section);
  }
  return (
    <Stack component="section" aria-label="Tournament draw" spacing={1.25}>
      <Typography component="h2" variant="h3">
        Draw
      </Typography>
      {matches.length ? (
        Array.from(phaseSections.entries()).map(([phase, phaseMatches]) => (
          <Box component="section" key={phase}>
            {bracketPhaseLabel(phase, phaseMatches[0]?.phase_label) ? (
              <Typography component="h3" sx={{ mb: 0.5 }} variant="h4">
                {bracketPhaseLabel(phase, phaseMatches[0]?.phase_label)}
              </Typography>
            ) : null}
            {phaseMatches.map((match) => (
              <Box
                key={match.match_id}
                sx={{
                  borderBottom: 1,
                  borderColor: "grey.500",
                  py: 1.25,
                }}
              >
                <Typography sx={{ fontWeight: 700 }} variant="h5">
                  Round {match.round_number} · Match {match.position}
                </Typography>
                <Typography variant="h6">
                  <span>
                    {match.entry1_display_name ?? "Awaiting qualifier"}
                  </span>{" "}
                  —{" "}
                  <span>
                    {match.entry2_display_name ?? "Awaiting qualifier"}
                  </span>
                </Typography>
                {match.status === "cancelled" ? (
                  <Typography color="text.secondary" variant="body1">
                    Not required
                  </Typography>
                ) : null}
                {match.score ? (
                  <Typography variant="body1">{match.score}</Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        ))
      ) : (
        <Typography variant="h5">
          The selected draw is awaiting matches.
        </Typography>
      )}
    </Stack>
  );
}

function StandingsRotation({ drawId }: { drawId: string | null }) {
  const standings = useQuery({
    queryKey: ["venue-display", "standings", drawId],
    queryFn: () => tournamentApi.getStandings(drawId as never),
    enabled: Boolean(drawId),
    retry: false,
  });
  if (!drawId) {
    return <Typography variant="h5">Select a draw from Displays.</Typography>;
  }
  if (standings.isPending)
    return <Typography role="status">Loading standings…</Typography>;
  if (standings.isError) {
    return (
      <Alert severity="error">
        The selected standings are temporarily unavailable.
      </Alert>
    );
  }
  return (
    <Stack component="section" aria-label="Tournament standings" spacing={1.5}>
      <Typography component="h2" variant="h3">
        Standings
      </Typography>
      {(standings.data?.groups ?? []).map((group) => (
        <Box key={group.group_number} component="section">
          <Typography sx={{ fontWeight: 700 }} variant="h5">
            Group {group.group_number}
          </Typography>
          <Stack component="ol" spacing={0.5} sx={{ m: 0, pl: 3 }}>
            {(group.rows ?? []).map((row) => (
              <Typography component="li" key={row.entry_id} variant="h6">
                {row.rank}. {row.display_name ?? "Player"} · {row.wins}–
                {row.losses}
              </Typography>
            ))}
          </Stack>
        </Box>
      ))}
      {!(standings.data?.groups ?? []).length ? (
        <Typography variant="h5">Standings are awaiting results.</Typography>
      ) : null}
    </Stack>
  );
}

function Ticker({ snapshot }: { snapshot: Snapshot }) {
  return (
    <Stack
      component="section"
      aria-label="Tournament announcements"
      spacing={2}
    >
      <Typography component="h2" variant="h3">
        Announcements
      </Typography>
      {snapshot.announcements.length ? (
        snapshot.announcements.map((announcement) => (
          <Typography key={announcement.announcement_id} variant="h4">
            {announcement.text}
          </Typography>
        ))
      ) : (
        <Typography variant="h5">No current announcements.</Typography>
      )}
    </Stack>
  );
}

export function VenueDisplayPage() {
  const { tournamentId = "" } = useParams<{ tournamentId: string }>();
  const display = useTournamentDisplayLive(tournamentId, {
    enabled: Boolean(tournamentId),
  });
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const snapshot = display.data;
  const displayStatus = (display.error as { status?: unknown } | undefined)
    ?.status;
  const revoked = displayStatus === 401 || displayStatus === 403;
  const selectedCourtId = snapshot?.display.selection.court_id ?? null;
  const courtCount = snapshot
    ? selectedCourtId
      ? 1
      : snapshot.courts.length
    : 0;
  const drawId = snapshot?.display.selection.draw_id ?? null;
  const hasAnnouncements = Boolean(snapshot?.announcements.length);
  const views = useMemo(
    () => displayViews({ courtCount, drawId, hasAnnouncements }),
    [courtCount, drawId, hasAnnouncements],
  );
  const activeView = views[activeViewIndex] ??
    views[0] ?? {
      rotation: "court" as Rotation,
      courtPage: 0,
    };

  useEffect(() => {
    if (!snapshot) return;
    const configuredIndex = views.findIndex(
      (view) => view.rotation === snapshot.display.selection.rotation,
    );
    setActiveViewIndex(configuredIndex >= 0 ? configuredIndex : 0);
  }, [snapshot?.display.revision, snapshot?.display.selection.rotation, views]);

  useEffect(() => {
    if (!snapshot?.display.auto_rotate || views.length < 2) return;
    const interval = window.setInterval(() => {
      setActiveViewIndex((current) => (current + 1) % views.length);
    }, AUTO_ROTATE_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [snapshot?.display.auto_rotate, views]);

  useEffect(() => {
    const syncFullscreen = () =>
      setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", syncFullscreen);
    syncFullscreen();
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  if (display.isError && revoked) {
    return (
      <DisplayShell>
        <Alert severity="warning" role="alert">
          This display session is no longer active. Ask an organizer to
          provision it again.
        </Alert>
      </DisplayShell>
    );
  }

  if (display.isPending || !snapshot) {
    if (display.isError) {
      return (
        <DisplayShell>
          <Alert severity="error" role="alert">
            The tournament display is temporarily unavailable.
          </Alert>
        </DisplayShell>
      );
    }
    return (
      <DisplayShell>
        <Stack
          direction="row"
          role="status"
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          <CircularProgress color="inherit" size={28} />
          <Typography variant="h5">Loading tournament display…</Typography>
        </Stack>
      </DisplayShell>
    );
  }

  const enterFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setFullscreen(false);
      } else {
        await document.documentElement.requestFullscreen();
        setFullscreen(true);
      }
    } catch {
      setFullscreen(Boolean(document.fullscreenElement));
    }
  };

  return (
    <DisplayShell>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" variant="h3">
              {snapshot.display.display_name}
            </Typography>
            <Typography color="grey.300" variant="h6">
              {activeView.rotation === "court"
                ? "Court board"
                : activeView.rotation === "draw"
                  ? "Draw"
                  : activeView.rotation === "standings"
                    ? "Standings"
                    : "Announcements"}
            </Typography>
          </Box>
          <Button
            color="inherit"
            onClick={() => void enterFullscreen()}
            startIcon={
              fullscreen ? (
                <FullscreenExitOutlinedIcon />
              ) : (
                <FullscreenOutlinedIcon />
              )
            }
            variant="outlined"
          >
            {fullscreen ? "Exit fullscreen" : "Fullscreen"}
          </Button>
        </Stack>
        {activeView.rotation === "court" ? (
          <CourtBoard snapshot={snapshot} page={activeView.courtPage} />
        ) : null}
        {activeView.rotation === "draw" ? (
          <DrawRotation drawId={drawId} />
        ) : null}
        {activeView.rotation === "standings" ? (
          <StandingsRotation drawId={drawId} />
        ) : null}
        {activeView.rotation === "ticker" ? (
          <Ticker snapshot={snapshot} />
        ) : null}
        {snapshot.announcements.length && activeView.rotation !== "ticker" ? (
          <Typography aria-live="polite" sx={{ fontWeight: 700 }} variant="h5">
            {snapshot.announcements[0].text}
          </Typography>
        ) : null}
      </Stack>
    </DisplayShell>
  );
}

function DisplayShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      aria-label="Venue display"
      component="main"
      sx={{
        bgcolor: "grey.900",
        color: "common.white",
        minHeight: "100vh",
        p: { xs: 2, sm: 3, lg: 5 },
      }}
    >
      {children}
    </Box>
  );
}
